import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import { DEVICE_NAME_MIN_LENGTH, DEVICE_NAME_MAX_LENGTH } from 'entities/device';
import {
	createEditDeviceMutation,
	Device,
	EditDeviceMutationParams,
	EntityWithName,
} from 'shared/api';
import {
	maxLengthRule,
	minLengthRule,
	notEmptyRule,
	notify,
	createModalFormModel,
	ModalFormModel,
} from 'shared/lib';

export type EditDeviceFormValue = {
	name: string;
};

export type EditDeviceModel = ModalFormModel<Device, EditDeviceFormValue> & {
	editDeviceMutation: Mutation<EditDeviceMutationParams, EntityWithName, JsonApiRequestError>;
	$pending: Store<boolean>;
};

export const createEditDeviceModel = (): EditDeviceModel => {
	const editDeviceMutation = createEditDeviceMutation();

	const model = createModalFormModel<Device, EditDeviceFormValue>({
		fields: {
			name: {
				init: '',
				rules: [
					notEmptyRule,
					minLengthRule(DEVICE_NAME_MIN_LENGTH),
					maxLengthRule(DEVICE_NAME_MAX_LENGTH),
				],
			},
		},
		validateOn: ['submit'],
		resetErrorsOnChange: true,
	});

	const { $pending } = editDeviceMutation;

	sample({
		clock: model.modalButtonClicked,
		target: [model.form.setValues, model.form.setInitialValues],
	});

	sample({
		clock: model.form.submitted,
		source: {
			values: model.form.$values,
			device: model.$modalData,
		},
		filter: ({ device }) => device !== null,
		fn: ({ values, device }) => ({
			id: device?.id ?? 0,
			name: values.name,
		}),
		target: editDeviceMutation.start,
	});

	chain(editDeviceMutation.finished.success, model.switcher.disable);

	notify(editDeviceMutation.finished.success, 'edit-device.success');

	chain(model.modalClosed, editDeviceMutation.reset);

	return {
		editDeviceMutation,
		$pending,
		...model,
	};
};
